// components/ThermalInvoice.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ThermalInvoice.css';

const ThermalInvoice = ({ bookingId }) => {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [error, setError] = useState(null);
  
  const API_BASE_URL = process.env.REACT_APP_API_PATH || 'http://localhost:8060/api';

  useEffect(() => {
    if (bookingId) {
      fetchInvoiceData();
    }
    checkPrinters();
  }, [bookingId]);

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fix: Use axios or proper fetch with .json()
      const response = await axios.get(`${API_BASE_URL}ThermalPrint/booking/${bookingId}/thermal`);
      
      // If using fetch instead:
      // const response = await fetch(`${API_BASE_URL}/ThermalPrint/booking/${bookingId}/thermal`);
      // const data = await response.json();
      // setInvoice(data);
      
      setInvoice(response.data);
      console.log('Invoice data loaded:', response.data);
    } catch (error) {
      console.error('Error fetching invoice:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load invoice data');
      alert('Failed to load invoice data: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const checkPrinters = async () => {
    // Check if browser supports WebUSB for thermal printers
    if ('usb' in navigator) {
      try {
        const printers = await getAvailablePrinters();
        setAvailablePrinters(printers);
        if (printers.length > 0) {
          setPrinterConnected(true);
        }
      } catch (error) {
        console.error('Error checking printers:', error);
      }
    }
  };

  const getAvailablePrinters = async () => {
    const printers = [];
    
    try {
      const device = await navigator.usb.requestDevice({
        filters: [
          { vendorId: 0x0b05 }, // Example: Epson
          { vendorId: 0x0416 }, // Example: Bixolon
          { vendorId: 0x0499 }, // Example: Star
          { classCode: 7 }, // Printer class
        ]
      });
      
      printers.push({
        id: device.serialNumber || 'usb-printer',
        name: device.productName || 'USB Thermal Printer',
        device: device
      });
    } catch (error) {
      console.log('No USB device selected or supported:', error);
    }
    
    return printers;
  };

  // Method 1: Print using QZ Tray (Recommended for desktop)
  const printWithQzTray = async () => {
    if (!window.qz) {
      alert('QZ Tray is not installed. Please install QZ Tray for thermal printing.\n\nDownload from: https://qz.io/download/');
      window.open('https://qz.io/download/', '_blank');
      return;
    }

    try {
      setLoading(true);
      await window.qz.websocket.connect();
      
      const response = await axios.get(`${API_BASE_URL}/ThermalPrint/booking/${bookingId}/thermal`);
      
      if (!response.data?.thermalData) {
        throw new Error('No thermal data received');
      }
      
      const config = window.qz.configs.create(selectedPrinter || 'thermal-printer');
      await window.qz.print(config, [response.data.thermalData]);
      await window.qz.websocket.disconnect();
      
      alert('Invoice printed successfully!');
    } catch (error) {
      console.error('Print error:', error);
      alert('Failed to print: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Method 2: Print using WebUSB directly
  const printWithWebUSB = async () => {
    try {
      setLoading(true);
      
      const device = await navigator.usb.requestDevice({
        filters: [
          { classCode: 7 }, // Printer class
        ]
      });
      
      await device.open();
      
      if (device.configuration === null) {
        await device.selectConfiguration(1);
      }
      
      await device.claimInterface(0);
      
      // Fetch thermal data as text
      const response = await axios.get(`${API_BASE_URL}/ThermalPrint/booking/${bookingId}/print`, {
        responseType: 'text'
      });
      
      const textEncoder = new TextEncoder();
      const data = textEncoder.encode(response.data);
      
      // Send to printer
      await device.transferOut(1, data);
      await device.close();
      
      alert('Invoice printed successfully!');
    } catch (error) {
      console.error('WebUSB print error:', error);
      alert('Failed to print via USB: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Method 3: Print using Browser's print (for development/testing)
  const printWithBrowser = () => {
    if (!invoice?.thermalData) {
      alert('No invoice data to print. Please load the invoice first.');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Thermal Invoice #${bookingId}</title>
          <style>
            @page {
              size: 58mm auto;
              margin: 0;
            }
            body {
              font-family: 'Courier New', 'Monaco', monospace;
              font-size: 11px;
              width: 58mm;
              margin: 0;
              padding: 3mm;
              line-height: 1.3;
            }
            pre {
              margin: 0;
              padding: 0;
              font-family: 'Courier New', monospace;
              font-size: 11px;
              white-space: pre-wrap;
              word-wrap: break-word;
            }
            .header { text-align: center; margin-bottom: 5px; }
            .divider { border-top: 1px dashed #000; margin: 5px 0; }
            .item-row { display: flex; justify-content: space-between; }
            .total { font-weight: bold; margin-top: 10px; }
            .footer { text-align: center; margin-top: 10px; }
          </style>
        </head>
        <body>
          <pre>${escapeHtml(invoice.thermalData)}</pre>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const escapeHtml = (text) => {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  // Method 4: Print via Electron IPC (for Electron apps)
  const printViaElectron = () => {
    if (window.electron && window.electron.print) {
      window.electron.print({
        bookingId: bookingId,
        printerName: selectedPrinter
      });
    } else {
      alert('Electron bridge not available');
    }
  };

  // Method 5: Direct ESC/POS printing using raw data
  const printRawEscPos = async () => {
    try {
      setLoading(true);
      
      // Fetch raw thermal data
      const response = await axios.get(`${API_BASE_URL}/ThermalPrint/booking/${bookingId}/print`, {
        responseType: 'blob'
      });
      
      // Create blob URL
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      
      // Create temporary link and click
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${bookingId}.txt`;
      link.click();
      
      URL.revokeObjectURL(url);
      
      alert('Thermal data downloaded. Send this file to your thermal printer using printer software.');
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download thermal data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading invoice data...</p>
      </div>
    );
  }

  return (
    <div className="thermal-invoice-container">
      <div className="thermal-controls">
        <h3>Thermal Printer Setup - Booking #{bookingId}</h3>
        
        {error && (
          <div className="error-message">
            ⚠️ {error}
            <button onClick={fetchInvoiceData} className="retry-btn">Retry</button>
          </div>
        )}
        
        <div className="printer-selection">
          <label>Select Printer:</label>
          <select 
            value={selectedPrinter} 
            onChange={(e) => setSelectedPrinter(e.target.value)}
            className="printer-select"
          >
            <option value="">Select a printer</option>
            <option value="thermal-printer">USB Thermal Printer (Default)</option>
            {availablePrinters.map(printer => (
              <option key={printer.id} value={printer.name}>
                {printer.name}
              </option>
            ))}
          </select>
        </div>

        <div className="print-methods">
          <button 
            onClick={printWithQzTray} 
            className="print-btn qz-btn"
            disabled={!selectedPrinter || loading}
          >
            🖨️ Print via QZ Tray
          </button>
          
          <button 
            onClick={printWithWebUSB} 
            className="print-btn usb-btn"
            disabled={loading}
          >
            🔌 Print via WebUSB
          </button>
          
          <button 
            onClick={printWithBrowser} 
            className="print-btn browser-btn"
            disabled={!invoice || loading}
          >
            🖥️ Print via Browser
          </button>
          
          <button 
            onClick={printRawEscPos} 
            className="print-btn download-btn"
            disabled={loading}
          >
            💾 Download Thermal Data
          </button>
          
          {window.electron && (
            <button 
              onClick={printViaElectron} 
              className="print-btn electron-btn"
              disabled={!selectedPrinter || loading}
            >
              ⚡ Print via Electron
            </button>
          )}
        </div>

        <div className="printer-info">
          <h4>📋 Printer Setup Guide:</h4>
          <ul>
            <li><strong>QZ Tray:</strong> Install QZ Tray for best thermal printing support (Windows/Mac/Linux)</li>
            <li><strong>WebUSB:</strong> Connect USB printer directly (Chrome/Edge only) - requires HTTPS</li>
            <li><strong>Browser Print:</strong> Use system print dialog (set paper size to 58mm x 297mm)</li>
            <li><strong>Download:</strong> Save raw ESC/POS data and print using printer software</li>
          </ul>
          <p className="printer-note">
            ⚡ <strong>Note:</strong> For production use, install <a href="https://qz.io/download/" target="_blank" rel="noopener noreferrer">QZ Tray</a> for reliable thermal printing.
          </p>
        </div>
      </div>

      <div className="invoice-preview">
        <h4>📄 Preview (58mm Thermal Format)</h4>
        <div className="thermal-preview-wrapper">
          <pre className="thermal-preview">
            {invoice?.thermalData || 'No invoice data loaded. Click refresh to load.'}
          </pre>
        </div>
        {invoice && (
          <div className="preview-actions">
            <button onClick={fetchInvoiceData} className="refresh-btn">
              🔄 Refresh Invoice
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ThermalInvoice;